import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { Customer, CustomerSearchData } from '../../models/customer.model';
import { revertAll, RootState } from '../../store';
import { CustomerNote } from '../../models/customer.note.model';

export const fetchCustomers = createAsyncThunk(
  'companies/fetchCustomers',
  async ({ company_id }: CustomerSearchData, { rejectWithValue }) => {
    if (company_id === undefined) {
      return rejectWithValue('Company ID is required');
    }
    const response = await NWClient.list('customer', { company_id });
    return response;
  }
);

export const updateCustomers = createAsyncThunk(
  'customers/updateCustomers',
  async (updatedCustomer: Customer, { rejectWithValue }) => {
    try {
      const response = await NWClient.put(
        'customer',
        updatedCustomer.id,
        updatedCustomer,
        true,
        false
      );
      return response as Customer;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCustomerNotes = createAsyncThunk(
  'customers/fetchCustomerNotes',
  async (customerId: number, { rejectWithValue }) => {
    if (customerId === undefined) {
      return rejectWithValue('Customer ID is required');
    }
    try {
      const response = (await NWClient.get('customer', customerId)) as Customer;
      return { customerId, notes: response.notes as CustomerNote[] };
    } catch (error) {
      return rejectWithValue('Failed to fetch customer notes');
    }
  }
);

export const updateCustomerNotes = createAsyncThunk(
  'customers/updateCustomerNotes',
  async ({ customerId, note }: { customerId: number; note: CustomerNote }, { dispatch }) => {
    try {
      await NWClient.put(`customer/${customerId}/notes`, note.id, note);
      // After successful update, fetch updated notes to refresh state
      await dispatch(fetchCustomerNotes(customerId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteCustomerNote = createAsyncThunk(
  'customers/deleteCustomerNote',
  async ({ customerId, noteId }: { customerId: number; noteId: number }, { dispatch }) => {
    try {
      await NWClient.delete(`customer-notes`, noteId);
      // After successful deletion, fetch updated notes to refresh state
      await dispatch(fetchCustomerNotes(customerId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export interface CustomersState {
  customersList: Customer[];
  status: string;
  error: string;
  notes: CustomerNote[];
}

const customersArray: Customer[] = [];
const initialState: CustomersState = {
  customersList: customersArray,
  status: 'idle',
  error: '',
  notes: [],
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomerNotes(state) {
      state.notes = []; // Reset notes to an empty array
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.customersList = [...action.payload] as Customer[];
          state.status = 'succeeded';
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateCustomers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCustomers.fulfilled, (state, action) => {
        const index = state.customersList.findIndex(
          (customer) => customer.id === action.payload.id
        );
        if (index !== -1) {
          state.customersList[index] = action.payload as Customer;
        }
        state.status = 'succeeded';
      })
      .addCase(updateCustomers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchCustomerNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomerNotes.fulfilled, (state, action) => {
        const { customerId, notes } = action.payload;
        state.notes = notes;
        state.status = 'succeeded';
      })
      .addCase(fetchCustomerNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch customer notes';
      })
      .addCase(updateCustomerNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCustomerNotes.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(updateCustomerNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to update customer note';
      })
      .addCase(deleteCustomerNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCustomerNote.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(deleteCustomerNote.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to delete customer note';
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { clearCustomerNotes } = customersSlice.actions;

export default customersSlice.reducer;

export const selectAllCustomers = (state: RootState) => state.customers.customersList;
export const selectCustomerById = (state: RootState, customerId: number) =>
  state.customers.customersList.find((item) => item.id === customerId);
export const selectCustomerNotes = (state: RootState) => state.customers.notes;
